"use client"

import { useState } from "react"
import { tutorialSlides } from "@/data/tutorial-slides"
import { TutorialSlideComponent } from "./tutorial-slide"

interface TutorialModalProps {
  isOpen: boolean
  onClose: () => void
}

export const TutorialModal = ({ isOpen, onClose }: TutorialModalProps) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)

  if (!isOpen) return null

  const currentSlide = tutorialSlides[currentSlideIndex]
  const isLastSlide = currentSlideIndex === tutorialSlides.length - 1

  const handleNext = () => {
    if (isLastSlide) {
      onClose()
    } else {
      setCurrentSlideIndex((prev) => prev + 1)
    }
  }

  const handleSkip = () => {
    onClose()
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-[100] backdrop-blur-sm animate-in fade-in duration-200"
        onClick={handleSkip}
      />

      {/* Tutorial Modal.
          The whole thing is a height-constrained flex column: header and the
          buttons never shrink, the slide area scrolls if it has to. Previously
          the parchment had a fixed min-height and the buttons hung below it, so
          on a landscape phone (~390px tall) the buttons were pushed completely
          off-screen and the tutorial could not be dismissed. */}
      <div className="fixed inset-0 z-[110] flex items-center justify-center p-2 md:p-4 pointer-events-none">
        <div
          className="relative flex max-h-full w-full max-w-4xl flex-col pointer-events-auto animate-in zoom-in-95 duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="h-9 md:h-14 shrink-0 flex items-center justify-center rounded-t-lg shadow-md"
            style={{
              background: "linear-gradient(to bottom, #6b3410, #4a2410)",
            }}
          >
            <h1
              className="text-base md:text-2xl font-bold italic tracking-wide"
              style={{
                color: "#f5e6d3",
                textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
              }}
            >
              INSTRUCTIONS
            </h1>
          </div>

          {/* Parchment Content */}
          <div
            className="relative min-h-0 flex-1 overflow-y-auto rounded-b-lg shadow-2xl"
            style={{
              background: "linear-gradient(to bottom, #f5e6d3, #e8d5b7)",
              borderLeft: "3px solid #c9a66b",
              borderRight: "3px solid #c9a66b",
              borderBottom: "3px solid #c9a66b",
            }}
          >
            {/* Content Area */}
            <div className="py-4 md:py-10 short:py-2">
              <TutorialSlideComponent slide={currentSlide} />
            </div>
          </div>

          {/* Progress dots */}
          <div className="flex shrink-0 justify-center gap-1.5 pt-2">
            {tutorialSlides.map((slide, index) => (
              <span
                key={slide.id}
                className={`h-1.5 rounded-full transition-all ${
                  index === currentSlideIndex ? "w-5 bg-amber-300" : "w-1.5 bg-amber-100/50"
                }`}
              />
            ))}
          </div>

          {/* Bottom Buttons - sticking out below parchment */}
          <div className="flex shrink-0 gap-3 md:gap-4 mt-2 md:mt-4 px-2 md:px-4">
            {/* Skip button */}
            <button
              onClick={handleSkip}
              className="flex-1 py-2 md:py-4 rounded-lg font-bold italic text-sm md:text-lg transition-all hover:brightness-110 active:scale-95 shadow-md"
              style={{
                background: "linear-gradient(to bottom, #8b7355, #6b5444)",
                color: "#f5e6d3",
                textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
              }}
            >
              Skip this!
            </button>

            {/* Next/Got it button */}
            <button
              onClick={handleNext}
              className="flex-1 py-2 md:py-4 rounded-lg font-bold italic text-sm md:text-lg transition-all hover:brightness-110 active:scale-95 shadow-md"
              style={{
                background: "linear-gradient(to bottom, #3b82f6, #2563eb)",
                color: "#ffffff",
                textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
              }}
            >
              {isLastSlide ? "Got it!" : "Next!"}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
